// Optimized salesService.ts with improved performance and refund handling

import   { API_URL } from "../apiUtils"
import storeService from "./storeService"
import axiosInstance from "../../CustomAxios/axiosInstance"



interface RefundResponse {
    success: boolean
    message: string
}

export interface SaleProduct {
    id: string
    image?: string
    name: string
    category: string
    brand: string
    quantity: number
    unitPrice: number // selling price
    buyingPrice: number
    discount: number
    totalPrice: number // after discount
    earnings: number // profit after all deductions
    earnings_net?: number // net profit after all deductions and fees
    status?: "Completed" | "Refunded" // Added status field
    loyaltyDiscount?: number
}

export interface Sale {
    id: string
    orderNumber: string
    date: string
    time: string
    client: string
    seller: string
    type: "POS" | "ONLINE"
    paymentMethod: "Cash" | "Card" | "Split"
    cardAmount?: number
    cashAmount?: number
    totalAmount: number
    totalDiscount: number
    loyaltyPointsDiscount?: number
    cmiDeduction?: number
    card_fee?: number
    totalEarnings: number
    status: "Completed" | "Pending" | "Cancelled" | "Refunded" | "Confirmed" | "Shipped"
    products: SaleProduct[]
}

// Define proper interface for order request
interface OrderRequest {
    client_id: number
    livraison_id?: number
    type: "POS" | "ONLINE"
    payment: "CMI" | "Cash" | "Split" 
    user_id: number
    items: {
        product_id: number
        quantity: number
        deduction: number
        stock_id?: number
    }[]
    cmiAmount?: number
    cashAmount?: number
    fidelite_points_used?: number
}

// Interface for receipt data
export interface ReceiptData {
    order: {
        id: number
        orderNumber?: string
        client: string
        paymentMethod: string
        totalAmount: number
        totalEarnings?: number
        products: {
            name: string
            quantity: number
            price: number
            total: number
            discount?: number
            loyaltyDiscount?: number
        }[]
        seller: string
        date: string
        time: string
        cmiAmount?: number
        cashAmount?: number
        loyaltyPointsUsed?: number
        loyaltyPointsDiscount?: number
    }
    data?: {
        calculation?: {
            card_deduction?: number
            subtotal?: number
            deductions?: number
            final_total?: number
            loyalty_points_discount?: number
        }
    }
}

// Updated API response interface to match the actual structure
interface ApiOrderResponse {
    status: string
    data: ApiOrder[]
}

// Updated ApiOrder interface to match the new API response structure
interface ApiOrder {
    id: number
    client_id: number
    client_name: string
    user_id: number
    user_name: string
    type: "POS" | "ONLINE"
    status: "Pending" | "Confirmed" | "Shipped" | "Cancelled" | "Refunded"
    payment: string
    total_ttc: string
    total_ht: string
    created_at: string
    updated_at: string
    cmiAmount: string
    cashAmount: string
    card_fee: string
    loyalty_point: number
    loyalty_point_value: string
    order_details: OrderDetail[]
}

// Updated OrderDetail interface to match the new API response structure
interface OrderDetail {
    id: number
    order_id: number
    quantity: number
    discount: string
    price_after_deduction: string
    refunded: number
    buy_price: string
    earning: string
    earning_net: number
    loyalty_point: number
    loyalty_point_gained: number
    product_id: number
    product_name: string
    product_image: string
}

// ======= IMPROVED CACHING MECHANISM =======
// Create a cache for sales data with proper typing
interface SalesCache {
    data: Sale[] | null
    timestamp: number
    expiryMs: number
    dateFilters: {
        [date: string]: Sale[]
    }
}

const salesCache: SalesCache = {
    data: null,
    timestamp: 0,
    expiryMs: 5 * 60 * 1000, // 5 minutes cache expiry
    dateFilters: {},
}

// Check if the cache is valid with improved logic
const isCacheValid = (): boolean => {
    return salesCache.data !== null && Date.now() - salesCache.timestamp < salesCache.expiryMs
}

// Check if a specific date filter is cached
const isDateCached = (date: string): boolean => {
    return salesCache.dateFilters[date] !== undefined && Date.now() - salesCache.timestamp < salesCache.expiryMs
}

// Invalidate the cache
const invalidateCache = (): void => {
    salesCache.data = null
    salesCache.timestamp = 0
    salesCache.dateFilters = {}
}

// Add a loyaltyPointValue variable to store the value of loyalty points
const loyaltyPointValue = 0.5 // Default value
let cardDeductionRate: number | null = null

// Add a function to load the loyalty point value
const loadLoyaltyPointValue = async (): Promise<number> => {
    try {
        const storeData = await storeService.getStoreData()
        // Add validation to ensure we have a reasonable value
        const pointValue = storeData.loyalty_point_value || 0.5

        // Ensure the value is within a reasonable range (0.01 to 5.00)
        if (pointValue < 0.01 || pointValue > 5) {
            console.warn(`Loyalty point value (${pointValue}) is outside reasonable range, using default 0.5`)
            return 0.5
        }

        return pointValue
    } catch (error) {
                return 0.5 // Default to 0.5 if there's an error
    }
}

// Helper function to get card deduction rate with caching
const getCardDeductionRate = async (): Promise<number> => {
    // Use cached value if available
    if (cardDeductionRate !== null) {
        return cardDeductionRate
    }

    try {
        const card_deduction = await storeService.getCardDeduction()
        cardDeductionRate = card_deduction || 0
        return cardDeductionRate
    } catch (error) {
                return 0 // Default to 0 if there's an error
    }
}

// Store for brandCache and categoryCache to avoid redundant API calls
// Use a more efficient Map for better performance
const brandCache = new Map<string, string>()
const categoryCache = new Map<string, string>()

// Preload common brands and categories
const preloadCommonData = () => {
    // Common brands
    const commonBrands = {
        "1": "Royal Canin",
        "2": "Purina",
        "3": "Pedigree",
        "4": "Whiskas",
        "5": "Hill's",
        "6": "Blue Buffalo",
        "7": "IAMS",
        "8": "Fancy Feast",
        "9": "Friskies",
        "10": "Meow Mix",
    }

    // Common categories
    const commonCategories = {
        "1": "Dogs",
        "2": "Cats",
        "3": "Birds",
        "4": "Fish",
        "5": "Small Pets",
        "6": "Dog Food",
        "7": "Cat Food",
        "8": "Pet Toys",
        "9": "Pet Accessories",
        "10": "Pet Supplies",
    }

    // Merge with brandCache and categoryCache
    Object.entries(commonBrands).forEach(([key, value]) => {
        brandCache.set(key, value)
    })

    Object.entries(commonCategories).forEach(([key, value]) => {
        categoryCache.set(key, value)
    })
}

// Initialize common data
preloadCommonData()

// Function to get brand names - optimized with Map for better performance
const getBrandName = async (brandId: number): Promise<string> => {
    // Skip invalid brand IDs
    if (!brandId || brandId <= 0) {
        return "Unknown Brand"
    }

    const cacheKey = brandId.toString()

    // Return from cache if available
    if (brandCache.has(cacheKey)) {
        return brandCache.get(cacheKey) || "Unknown Brand"
    }

    try {
        // Try to get brand directly from API using axios instance
        const response = await axiosInstance.get(`/entities/brands/${brandId}`)

        // Handle various response structures
        let brandName = `Brand-${brandId}`

        if (response.data && response.data.data && response.data.data.name) {
            brandName = response.data.data.name
        } else if (response.data && response.data.name) {
            brandName = response.data.name
        }

        // Save to cache
        brandCache.set(cacheKey, brandName)
        return brandName
    } catch (error: any) {
        // Handle not found errors
        if (
            error.message &&
            (error.message.includes("not found") ||
                error.message.includes("404") ||
                (error.response && error.response.status === 404))
        ) {
            const fallbackName = `Unknown Brand (${brandId})`
            brandCache.set(cacheKey, fallbackName)
            return fallbackName
        }

                return `Brand-${brandId}`
    }
}

// Function to get category names - optimized with Map for better performance
const getCategoryName = async (categoryId: number): Promise<string> => {
    // Skip invalid category IDs
    if (!categoryId || categoryId <= 0) {
        return "Unknown Category"
    }

    const cacheKey = categoryId.toString()

    // Return from cache if available
    if (categoryCache.has(cacheKey)) {
        return categoryCache.get(cacheKey) || "Unknown Category"
    }

    try {
        // Try to get category directly from API using axios instance
        const response = await axiosInstance.get(`/entities/categories/${categoryId}`)

        // Handle various response structures
        let categoryName = `Category-${categoryId}`

        if (response.data && response.data.data && response.data.data.name) {
            categoryName = response.data.data.name
        } else if (response.data && response.data.name) {
            categoryName = response.data.name
        }

        // Save to cache
        categoryCache.set(cacheKey, categoryName)
        return categoryName
    } catch (error: any) {
        // Handle not found errors
        if (
            error.message &&
            (error.message.includes("not found") ||
                error.message.includes("404") ||
                (error.response && error.response.status === 404))
        ) {
            const fallbackName = `Unknown Category (${categoryId})`
            categoryCache.set(cacheKey, fallbackName)
            return fallbackName
        }

                return `Category-${categoryId}`
    }
}

// Convert API order format to our Sale format - optimized for performance
const convertApiOrderToSale = async (order: ApiOrder): Promise<Sale> => {
    // Extract just the date part and format it
    const createdDate = new Date(order.created_at)
    const formattedDate = createdDate.toISOString().split("T")[0]

    // Extract time part and format it
    const hours = createdDate.getHours().toString().padStart(2, "0")
    const minutes = createdDate.getMinutes().toString().padStart(2, "0")
    const formattedTime = `${hours}:${minutes}`

    // Calculate total discount from all order details
    const totalDiscount = order.order_details.reduce((sum, detail) => sum + Number.parseFloat(detail.discount || "0"), 0)

    // Calculate total earnings from all non-refunded order details
    const totalEarnings = order.order_details
        .filter((detail) => detail.refunded === 0)
        .reduce((sum, detail) => sum + Number.parseFloat(detail.earning || "0"), 0)

    // Parse card and cash amounts
    const cardAmount = Number.parseFloat(order.cmiAmount || "0")
    const cashAmount = Number.parseFloat(order.cashAmount || "0")

    // Calculate CMI deduction if card payment was used
    const cmiDeduction = order.payment === "CMI" ? Number.parseFloat(order.card_fee || "0") : undefined

    // Calculate loyalty points discount
    const loyaltyPointsDiscount = Number.parseFloat(order.loyalty_point_value || "0")

    // Process all order details
    const products: SaleProduct[] = order.order_details.map((detail) => {
        // Calculate the original unit price before any discounts
        const originalTotal = Number.parseFloat(detail.price_after_deduction || "0") + Number.parseFloat(detail.discount || "0")
        const unitPrice = detail.quantity ? originalTotal / detail.quantity : 0

        const buyingPrice = Number.parseFloat(detail.buy_price || "0") / (detail.quantity || 1)
        const discountAmount = Number.parseFloat(detail.discount || "0")
        const totalPrice = Number.parseFloat(detail.price_after_deduction || "0")
        const earnings = Number.parseFloat(detail.earning || "0")
        const earnings_net = detail.earning_net || earnings

        // Set status based on refunded flag
        const productStatus = detail.refunded === 1 ? ("Refunded" as const) : ("Completed" as const)

        return {
            id: detail.id.toString(),
            image: detail.product_image ? `${API_URL}/storage/${detail.product_image}` : undefined,
            name: detail.product_name,
            category: "Unknown Category", // This information is not available in the new API response
            brand: "Unknown Brand", // This information is not available in the new API response
            quantity: detail.quantity || 0,
            unitPrice: unitPrice,
            buyingPrice: buyingPrice,
            discount: discountAmount,
            loyaltyDiscount: detail.loyalty_point > 0 ? Number((detail.loyalty_point * 0.5).toFixed(2)) : 0,
            totalPrice: totalPrice,
            earnings: earnings,
            earnings_net: earnings_net,
            status: productStatus,
        }
    })

    // Check if all products are refunded to determine overall sale status
    const allProductsRefunded = products.every((product) => product.status === "Refunded")
    const saleStatus = allProductsRefunded ? "Refunded" : order.status

    return {
        id: order.id.toString(),
        orderNumber: `ORD-${order.id.toString().padStart(4, "0")}`,
        date: formattedDate,
        time: formattedTime,
        client: order.client_name || "Unknown Client",
        seller: order.user_name || "Unknown Seller",
        type: order.type || "POS",
        paymentMethod: order.payment === "CMI" ? "Card" : order.payment === "Split" ? "Split" : "Cash",
        cardAmount: cardAmount > 0 ? cardAmount : undefined,
        cashAmount: cashAmount > 0 ? cashAmount : undefined,
        totalAmount: Number.parseFloat(order.total_ttc || "0"),
        totalDiscount: totalDiscount,
        loyaltyPointsDiscount: loyaltyPointsDiscount > 0 ? Number(loyaltyPointsDiscount.toFixed(2)) : 0,
        cmiDeduction: cmiDeduction,
        card_fee: Number.parseFloat(order.card_fee || "0"),
        totalEarnings: totalEarnings,
        status: saleStatus,
        products: products,
    }
}

// Improved sales service with better performance and error handling
const salesService = {
    // Get sales with improved caching for better performance
    async getSales(date?: string): Promise<Sale[]> {
        // If a specific date is requested and it's cached, return from cache
        if (date && isDateCached(date)) {
            return salesCache.dateFilters[date]
        }

        // Return data from cache if it's valid and there's no specific date filter
        if (!date && isCacheValid() && salesCache.data) {
            return salesCache.data
        }

        try {
            // Use axios instance directly for better integration
            const response = await axiosInstance.get(`/orders`)

            if (!response.data) {
                throw new Error("Unexpected response format from server")
            }

            // Handle different response formats
            let ordersData: ApiOrder[] = []

            if (response.data.status === "success" && Array.isArray(response.data.data)) {
                ordersData = response.data.data
            } else {
                                throw new Error("Unable to parse orders data from server response")
            }

            // Process all orders in parallel with Promise.all
            const sales = await Promise.all(ordersData.map((order) => convertApiOrderToSale(order)))

            // Sort by date and time (most recent first)
            sales.sort((a, b) => {
                const dateA = new Date(`${a.date}T${a.time}`)
                const dateB = new Date(`${b.date}T${b.time}`)
                return dateB.getTime() - dateA.getTime()
            })

            // Update the cache with the full dataset
            salesCache.data = sales
            salesCache.timestamp = Date.now()

            // If date filter is provided, filter after sorting and cache the result
            if (date) {
                const filteredSales = sales.filter((sale) => sale.date === date)
                salesCache.dateFilters[date] = filteredSales
                return filteredSales
            }

            return sales
        } catch (error) {
                        throw error instanceof Error ? error : new Error("Failed to fetch sales data from server")
        }
    },

    // Get a single sale by ID with improved caching
    async getSale(id: string): Promise<Sale | undefined> {
        // Check cache first
        if (isCacheValid() && salesCache.data) {
            const cachedSale = salesCache.data.find((sale) => sale.id === id)
            if (cachedSale) {
                return cachedSale
            }
        }

        try {
            const sales = await this.getSales()
            return sales.find((sale) => sale.id === id)
        } catch (error) {
                        throw error instanceof Error ? error : new Error("Failed to fetch sale")
        }
    },

    // Create a new order
    async createOrder(
        clientId: string,
        products: {
            product_id: string
            quantity: number
            deduction: number
            stock_id?: number
        }[],
        paymentMethod: string,
        cmiAmount: number | null,
        cashAmount: number | null,
        fidelityPointsUsed: number | null,
        userId: string | number,
        orderType: "POS" | "ONLINE" = "POS",
    ): Promise<any> {
        try {
            // Validate product deductions
            for (const product of products) {
                if (product.deduction < 0) {
                    throw new Error("Discount cannot be negative")
                }
            }

           
            const payment = paymentMethod as "CMI" | "Cash" | "Split"

            // Prepare the request payload
            const orderData: OrderRequest = {
                client_id: Number(clientId),
                type: orderType,
                payment,
                user_id: Number(userId),
                items: products.map((item) => ({
                    product_id: Number(item.product_id),
                    quantity: item.quantity,
                    deduction: item.deduction || 0,
                    stock_id: item.stock_id,
                })),
            }

            // Add conditional payment amounts based on payment method
            if (payment == "Split") {
                orderData.cmiAmount = cmiAmount || 0
                orderData.cashAmount = cashAmount || 0
            } else if (payment == "CMI") {
                orderData.cmiAmount = cmiAmount || 0
                orderData.cashAmount = 0
            } else if (payment == "Cash") {
                orderData.cashAmount = cashAmount || 0
                orderData.cmiAmount = 0
            }

            // Add fidelity points if used
            if (fidelityPointsUsed && fidelityPointsUsed > 0) {
                orderData.fidelite_points_used = fidelityPointsUsed
            }

            // Make the API call to create the order using axios instance
                        const response = await axiosInstance.post("/orders/process", orderData)

            // Check if we have the expected response structure
            if (!response.data || !response.data.data || !response.data.data.order) {
                                throw new Error("Unexpected response format from server")
            }

            // Invalidate the sales cache since we've added a new order
            invalidateCache()

            // Process the response to create receipt data
            const orderResponse = response.data.data.order
            const calculationData = response.data.data.calculation || {}

            // Extract loyalty points information if available
            const loyaltyPointsUsed = orderData.fidelite_points_used || 0
            const loyaltyPointsDiscount = loyaltyPointsUsed * 0.5

            // Create a structured receipt for the frontend
            const receipt: ReceiptData = {
                order: {
                    id: orderResponse.id,
                    orderNumber: `ORD-${orderResponse.id.toString().padStart(4, "0")}`,
                    client: orderResponse.client?.name || "Guest",
                    paymentMethod: payment,
                    totalAmount: calculationData.final_total || orderResponse.total_ttc || 0,
                    totalEarnings: (calculationData.total_after_discounts || 0) - (calculationData.card_deduction || 0),
                    // Safely handle the orderDetails mapping
                    products: Array.isArray(orderResponse.orderDetails)
                        ? orderResponse.orderDetails.map((detail: any) => ({
                            name: detail.product_info?.name || detail.stock?.product?.name || "Unknown Product",
                            quantity: detail.quantity || 0,
                            price: Number.parseFloat(detail.price_after_deduction || "0") / detail.quantity || 0,
                            total: Number.parseFloat(detail.price_after_deduction || "0"),
                            discount: Number.parseFloat(detail.discount || "0"),
                            // Add loyalty points discount if available
                            loyaltyDiscount:
                                loyaltyPointsUsed > 0
                                    ? (Number.parseFloat(detail.price_after_deduction || "0") /
                                        (calculationData.total_after_discounts || orderResponse.total_ttc)) *
                                    loyaltyPointsDiscount
                                    : 0,
                        }))
                        : [],
                    seller: orderResponse.user?.name || "Staff",
                    date: new Date().toISOString().split("T")[0],
                    time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                    cmiAmount: orderResponse.cmiAmount || undefined,
                    cashAmount: orderResponse.cashAmount || undefined,
                    loyaltyPointsUsed: loyaltyPointsUsed > 0 ? loyaltyPointsUsed : undefined,
                    loyaltyPointsDiscount: loyaltyPointsDiscount > 0 ? loyaltyPointsDiscount : undefined,
                },
                data: {
                    calculation: calculationData,
                },
            }
                        return response.data
        } catch (error: any) {
            
            // Extract validation errors if available
            if (error.response && error.response.data && error.response.data.errors) {
                const errorMessages = Object.values(error.response.data.errors).flat().join(", ")
                throw new Error(`Validation failed: ${errorMessages}`)
            }

            if (error.response && error.response.data && error.response.data.message) {
                throw new Error(error.response.data.message)
            }

            throw error instanceof Error ? error : new Error("Failed to create order. Please try again.")
        }
    },

    // Improved product refund with better error handling and retry mechanism
    async processProductRefund(
        saleId: string,
        productId: string,
        passcode: string,
        reason: string,
    ): Promise<RefundResponse> {
        try {
            // Make the API call to process the product refund using axios instance
            const response = await axiosInstance.post("/refunds/order-detail", {
                order_detail_id: productId,
                refund_reason: reason,
                admin_password: passcode,
            })

            // Invalidate the cache when a refund is processed
            invalidateCache()

            // Check response status
            const isSuccess =
                response.data?.status === "success" ||
                response.status === 200 ||
                (response.data?.data && response.data?.data?.status === "success")

            if (isSuccess) {
                return {
                    success: true,
                    message: response.data?.message || "Product refunded successfully",
                }
            } else {
                console.warn("Refund API returned unsuccessful response:", response.data)
                return {
                    success: false,
                    message: response.data?.message || "Failed to process refund",
                }
            }
        } catch (error: any) {
            
            // Handle rate limit errors specifically
            if (error.message && error.message.includes("rate limit")) {
                return {
                    success: false,
                    message: "Server is busy. Please try again in a few moments.",
                }
            }

            return {
                success: false,
                message: error instanceof Error ? error.message : "An unexpected error occurred",
            }
        }
    },

    // Improved order refund with better error handling and retry mechanism
    async processOrderRefund(orderId: string, passcode: string, reason: string): Promise<RefundResponse> {
        try {
            // Make the API call to process the order refund using axios instance
            const response = await axiosInstance.post("/refunds/order", {
                order_id: orderId,
                refund_reason: reason,
                admin_password: passcode,
            })

            // Invalidate the cache when a refund is processed
            invalidateCache()

            // Check response status
            const isSuccess =
                response.data?.status === "success" ||
                response.status === 200 ||
                (response.data?.data && response.data?.data?.status === "success")

            if (isSuccess) {
                return {
                    success: true,
                    message: response.data?.message || "Order refunded successfully",
                }
            } else {
                console.warn("Refund API returned unsuccessful response:", response.data)
                return {
                    success: false,
                    message: response.data?.message || "Failed to process order refund",
                }
            }
        } catch (error: any) {
            
            // Handle rate limit errors specifically
            if (error.message && error.message.includes("rate limit")) {
                return {
                    success: false,
                    message: "Server is busy. Please try again in a few moments.",
                }
            }

            return {
                success: false,
                message: error instanceof Error ? error.message : "An unexpected error occurred",
            }
        }
    },

    // Check if a product is already refunded - optimized with caching
    async isProductRefunded(saleId: string, productId: string): Promise<boolean> {
        try {
            // First check if we have the sale in cache
            const sale = await this.getSale(saleId)
            if (sale) {
                const product = sale.products.find((p) => p.id === productId)
                if (product) {
                    return product.status === "Refunded"
                }
            }

            // Only call API if we couldn't determine from cache
            try {
                const response = await axiosInstance.get(`/refunds/check/${productId}`)
                return response.data && response.data.refunded === 1
            } catch (error) {
                                // Fall back to assuming not refunded if API check fails
                return false
            }
        } catch (error) {
                        return false // Default to not refunded if there's an error
        }
    },

    // Export sales report as PDF or CSV
    async exportSalesReport(
        startDate: string,
        endDate: string,
        format: "csv" | "pdf" = "pdf",
        filters: { status?: string; payment_method?: string; seller?: string } = {},
    ): Promise<Blob> {
        try {
            // Build the query parameters
            const params = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                format: format,
            })

            // Add optional filters
            if (filters.status) {
                params.append("status", filters.status)
            }
            if (filters.payment_method) {
                params.append("payment_method", filters.payment_method)
            }
            if (filters.seller) {
                params.append("seller", filters.seller)
            }

            // Use axios instance for the export request
            const response = await axiosInstance.get(`/orders/export?${params.toString()}`, {
                responseType: 'blob'
            })

            // Return the blob for download
            return response.data
        } catch (error) {
                        throw error instanceof Error ? error : new Error("Failed to export sales report")
        }
    },

    // Force refresh data and clear cache
    async forceRefresh(): Promise<Sale[]> {
        invalidateCache()
        return this.getSales()
    },

    // Download a PDF for a specific order
    async downloadOrderPDF(orderId: string): Promise<void> {
        try {
            const response = await axiosInstance.get(`/orders/${orderId}/pdf`, {
                responseType: 'blob'
            });

            // Create a blob from the response
            const blob = new Blob([response.data], { type: 'application/pdf' });
            
            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);
            
            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = `order_${orderId}.pdf`;
            
            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
                        throw error;
        }
    },
}

export default salesService